import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { GrUserAdmin } from 'react-icons/gr'
import { BiUser } from 'react-icons/bi'
import { BsEye, BsEyeSlash } from 'react-icons/bs'
import Swal from 'sweetalert2'
import Loader from '../components/Loader'
import axios from 'axios'

const Signup = ({ route }) => {
  const navigate = useNavigate()

  // State Management
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [firstname, setFirstname] = useState('')
  const [username, setUserName] = useState('')
  const [lastname, setLastname] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loader, setLoader] = useState(false)
  const [errors, setErrors] = useState({})

  // Toast Configuration
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer)
      toast.addEventListener('mouseleave', Swal.resumeTimer)
    }
  })

  // Form Validation
  const validateForm = () => {
    const newErrors = {}

    if (!firstname.trim()) {
      newErrors.firstname = 'First name is required'
    }

    if (!lastname.trim()) {
      newErrors.lastname = 'Last name is required'
    }

    if (!username.trim()) {
      newErrors.username = 'Username is required'
    } else if (username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters'
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      newErrors.email = 'Invalid email address'
    }

    if (!password) {
      newErrors.password = 'Password is required'
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Signup Function
  const handleSignup = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      Toast.fire({
        icon: 'error',
        title: 'Please fix the form errors'
      })
      return
    }

    setLoader(true)

    try {
      const referringUser = localStorage.getItem('referredUser');
      const response = await axios.post(`${route}/auth/register`, {
        firstName: firstname,
        lastName: lastname,
        userName: username,
        password: password,
        email: email.toLowerCase(),
        referralLink: referringUser || null
      })

      const result = response.data
      
      if (result.status === 'error') {
        setLoader(false)
        Toast.fire({
          icon: 'error',
          title: result.message
        })
        return
      }

      // Save token
      localStorage.setItem('token', result.token)

      // Prepare email data


      // Success notification
      Toast.fire({
        icon: 'success',
        title: 'Account created successfully'
      })

      // Clear form and localStorage
      setFirstname('')
      setLastname('')
      setUserName('')
      setEmail('')
      setPassword('')
      setConfirmPassword('')
      localStorage.removeItem('referredUser')

      navigate('/dashboard')
    } catch (error) {
      setLoader(false)
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.message || 'Registration failed. Please try again.'
      })
    }
  }

  return (
    <main className='signup-page'>
      {loader && <Loader />}
      
      <div className="login-wrapper">
        <form className="form" onSubmit={handleSignup}>
          <img 
            src="/full-logo-black.png" 
            alt="Logo" 
            className="signup-logo" 
            onClick={() => navigate('/')}
          />
          
          <span className="subtitle">
            Get started with Quantum Assets Global Ledger, just create an account and enjoy the experience.
          </span>

          {/* First Name Input */}
          <div className="input_containers">
            <label className="input_labels" htmlFor="firstname_field">First name</label>
            <span className="icont"><BiUser /></span>
            <input
              id="firstname_field"
              type="text"
              className={`input_field ${errors.firstname ? 'error' : ''}`}
              placeholder="John"
              value={firstname}
              onChange={(e) => setFirstname(e.target.value.trim())}
              required
            />
            {errors.firstname && <span className="error-message">{errors.firstname}</span>}
          </div>

          {/* Last Name Input */}
          <div className="input_containers">
            <label className="input_labels" htmlFor="lastname_field">Last name</label>
            <span className="icont"><BiUser /></span>
            <input
              id="lastname_field"
              type="text"
              className={`input_field ${errors.lastname ? 'error' : ''}`}
              placeholder="Doe"
              value={lastname}
              onChange={(e) => setLastname(e.target.value.trim())}
              required
            />
            {errors.lastname && <span className="error-message">{errors.lastname}</span>}
          </div>

          {/* Username Input */}
          <div className="input_containers">
            <label className="input_labels" htmlFor="username_field">Username</label>
            <span className="icont"><GrUserAdmin /></span>
            <input
              id="username_field"
              type="text"
              className={`input_field ${errors.username ? 'error' : ''}`}
              placeholder="johnsmith"
              value={username}
              onChange={(e) => setUserName(e.target.value.trim())}
              required
            />
            {errors.username && <span className="error-message">{errors.username}</span>}
          </div>

          {/* Email Input */}
          <div className="input_containers">
            <label className="input_labels" htmlFor="email_field">Email</label>
            <svg className="icont" {...emailSvgProps} />
            <input
              id="email_field"
              type="email"
              className={`input_field ${errors.email ? 'error' : ''}`}
              placeholder="name@mail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              required
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          {/* Password Input */}
          <div className="input_containers">
            <label className="input_labels" htmlFor="password_field">Password</label>
            <svg className="icont" {...passwordSvgProps} />
            <input
              id="password_field"
              type={showPassword ? "text" : "password"}
              className={`input_field ${errors.password ? 'error' : ''}`}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value.trim())}
              required
            />
            <div className="eye-container" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <BsEye /> : <BsEyeSlash />}
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          {/* Confirm Password Input */}
          <div className="input_containers">
            <label className="input_labels" htmlFor="confirm_password_field">Confirm Password</label>
            <svg className="icont" {...passwordSvgProps} />
            <input
              id="confirm_password_field"
              type={showConfirmPassword ? "text" : "password"}
              className={`input_field ${errors.confirmPassword ? 'error' : ''}`}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value.trim())}
              required
            />
            <div className="eye-container" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
              {showConfirmPassword ? <BsEye /> : <BsEyeSlash />}
            </div>
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          <span className="sub">
            Already have an account? <Link to="/login">Sign in</Link>
          </span>

          <button type="submit">Register</button>
        </form>
      </div>
    </main>
  )
}

// SVG Props
const emailSvgProps = {
  fill: "none",
  viewBox: "0 0 24 24",
  height: "24",
  width: "24",
  xmlns: "http://www.w3.org/2000/svg",
  children: [
    <path key="1" strokeLinejoin="round" strokeLinecap="round" strokeWidth="1.5" stroke="#141B34" d="M7 8.5L9.94202 10.2394C11.6572 11.2535 12.3428 11.2535 14.058 10.2394L17 8.5" />,
    <path key="2" strokeLinejoin="round" strokeWidth="1.5" stroke="#141B34" d="M2.01577 13.4756C2.08114 16.5412 2.11383 18.0739 3.24496 19.2094C4.37608 20.3448 5.95033 20.3843 9.09883 20.4634C11.0393 20.5122 12.9607 20.5122 14.9012 20.4634C18.0497 20.3843 19.6239 20.3448 20.7551 19.2094C21.8862 18.0739 21.9189 16.5412 21.9842 13.4756C22.0053 12.4899 22.0053 11.5101 21.9842 10.5244C21.9189 7.45886 21.8862 5.92609 20.7551 4.79066C19.6239 3.65523 18.0497 3.61568 14.9012 3.53657C12.9607 3.48781 11.0393 3.48781 9.09882 3.53656C5.95033 3.61566 4.37608 3.65521 3.24495 4.79065C2.11382 5.92608 2.08114 7.45885 2.01576 10.5244C1.99474 11.5101 1.99475 12.4899 2.01577 13.4756Z" />
  ]
}

const passwordSvgProps = {
  fill: "none",
  viewBox: "0 0 24 24",
  height: "24",
  width: "24",
  xmlns: "http://www.w3.org/2000/svg",
  children: [
    <path key="1" strokeLinecap="round" strokeWidth="1.5" stroke="#141B34" d="M18 11.0041C17.4166 9.91704 16.273 9.15775 14.9519 9.0993C13.477 9.03404 11.9788 9 10.329 9C8.67911 9 7.18091 9.03404 5.70604 9.0993C3.95328 9.17685 2.51295 10.4881 2.27882 12.1618C2.12602 13.2541 2 14.3734 2 15.5134C2 16.6534 2.12602 17.7727 2.27882 18.865C2.51295 20.5387 3.95328 21.8499 5.70604 21.9275C6.42013 21.9591 7.26041 21.9834 8 22" />,
    <path key="2" strokeLinejoin="round" strokeLinecap="round" strokeWidth="1.5" stroke="#141B34" d="M6 9V6.5C6 4.01472 8.01472 2 10.5 2C12.9853 2 15 4.01472 15 6.5V9" />,
    <path key="3" fill="#141B34" d="M21.2046 15.1045L20.6242 15.6956V15.6956L21.2046 15.1045ZM21.4196 16.4767C21.7461 16.7972 22.2706 16.7924 22.5911 16.466C22.9116 16.1395 22.9068 15.615 22.5804 15.2945L21.4196 16.4767ZM18.0228 15.1045L17.4424 14.5134V14.5134L18.0228 15.1045ZM18.2379 18.0387C18.5643 18.3593 19.0888 18.3545 19.4094 18.028C19.7299 17.7016 19.7251 17.1771 19.3987 16.8565L18.2379 18.0387ZM14.2603 20.7619C13.7039 21.3082 12.7957 21.3082 12.2394 20.7619L11.0786 21.9441C12.2794 23.1232 14.2202 23.1232 15.4211 21.9441L14.2603 20.7619ZM12.2394 20.7619C11.6914 20.2239 11.6914 19.358 12.2394 18.82L11.0786 17.6378C9.86927 18.8252 9.86927 20.7567 11.0786 21.9441L12.2394 20.7619ZM12.2394 18.82C12.7957 18.2737 13.7039 18.2737 14.2603 18.82L15.4211 17.6378C14.2202 16.4587 12.2794 16.4587 11.0786 17.6378L12.2394 18.82ZM14.2603 18.82C14.8082 19.358 14.8082 20.2239 14.2603 20.7619L15.4211 21.9441C16.6304 20.7567 16.6304 18.8252 15.4211 17.6378L14.2603 18.82ZM20.6242 15.6956L21.4196 16.4767L22.5804 15.2945L21.785 14.5134L20.6242 15.6956ZM15.4211 18.82L17.8078 16.4767L16.647 15.2944L14.2603 17.6377L15.4211 18.82ZM17.8078 16.4767L18.6032 15.6956L17.4424 14.5134L16.647 15.2945L17.8078 16.4767ZM16.647 16.4767L18.2379 18.0387L19.3987 16.8565L17.8078 15.2945L16.647 16.4767ZM21.785 14.5134C21.4266 14.1616 21.0998 13.8383 20.7993 13.6131C20.4791 13.3732 20.096 13.1716 19.6137 13.1716V14.8284C19.6145 14.8284 19.619 14.8273 19.6395 14.8357C19.6663 14.8466 19.7183 14.8735 19.806 14.9391C19.9969 15.0822 20.2326 15.3112 20.6242 15.6956L21.785 14.5134ZM18.6032 15.6956C18.9948 15.3112 19.2305 15.0822 19.4215 14.9391C19.5091 14.8735 19.5611 14.8466 19.5879 14.8357C19.6084 14.8273 19.6129 14.8284 19.6137 14.8284V13.1716C19.1314 13.1716 18.7483 13.3732 18.4281 13.6131C18.1276 13.8383 17.8008 14.1616 17.4424 14.5134L18.6032 15.6956Z" />
  ]
}

export default Signup




